/**
 * The rules that read the `/api/store/sync-status/` payload, pure and
 * unit-tested apart from the places that show them: the sidebar's
 * `SyncProgressPill` (every import, behind the app), the Home tiles, the
 * price-wait screen in front of the paywall (`components/sync/SyncScreen`,
 * `components/entry/entryScreen.js` PRICE_IMPORT) and the poll rate of the one
 * query they share (`hooks/useSyncStatus`).
 *
 * Two statuses are terminal — `completed` and `failed`; everything else
 * is `SyncRun.SYNCING_STATUSES`, the backend's own single source of
 * truth for "still going" (shopify_app/models.py).
 */

const TERMINAL_STATUSES = ['completed', 'failed'];

export function isSyncFinished(status) {
  return TERMINAL_STATUSES.includes(status);
}

/**
 * The phases a run walks before its orders, each with the `progress`
 * counters it fills (`sync_status_payload`). Every counter carries `synced`
 * and `skipped`; only products and variants carry `refreshed`.
 */
export const CATALOG_PHASES = [
  { id: 'locations', status: 'syncing_locations', counters: ['locations'] },
  { id: 'products', status: 'syncing_products_bulk', counters: ['products', 'variants'] },
  { id: 'inventory', status: 'syncing_inventory_bulk', counters: ['inventory'] },
];

const COUNTERS_WITH_REFRESHED = new Set(['products', 'variants']);

const RUN_ORDER = [
  'syncing_locations', 'syncing_products_bulk', 'syncing_inventory_bulk',
  'syncing_orders_bulk', 'completed',
];

/**
 * Every record the run visited for one counter: written, already fresh, or
 * stamp-only. A first import leaves the last two at 0.
 */
export function visitedCount(progress, name) {
  const counter = progress[name];
  return counter.synced + counter.skipped
    + (COUNTERS_WITH_REFRESHED.has(name) ? counter.refreshed : 0);
}

/** The records the run wrote with real changes, for one counter. */
export function changedCount(progress, name) {
  return progress[name].synced;
}

/**
 * Where one catalog phase of `sync` stands: 'pending', 'active' or
 * 'completed'. A queued run is about to start its locations. A failed run
 * stopped somewhere unknown, so a phase that visited records before it
 * failed counts as got through.
 *
 * @param {{status: string}} phase  a CATALOG_PHASES entry
 * @param {object} sync  the payload's `sync`
 */
export function phaseState(phase, sync) {
  const { status, progress } = sync;
  if (status === 'queued') return phase.id === 'locations' ? 'active' : 'pending';
  if (status === 'failed') {
    const visited = phase.counters.reduce((sum, name) => sum + visitedCount(progress, name), 0);
    return visited > 0 ? 'completed' : 'pending';
  }
  const phaseIndex = RUN_ORDER.indexOf(phase.status);
  const currentIndex = RUN_ORDER.indexOf(status);
  if (currentIndex > phaseIndex) return 'completed';
  if (currentIndex === phaseIndex) return 'active';
  return 'pending';
}

/**
 * Every catalog phase of a failed run got through: what failed is the
 * order history after them.
 */
export function catalogImported(sync) {
  return CATALOG_PHASES.every((phase) => phaseState(phase, sync) === 'completed');
}

/**
 * The store's first import is still bringing in its catalog: the app is
 * open, and a value summed over the stock so far would read as the store's.
 * A refresh updates data the store already has, so it never counts.
 *
 * @param {object|null} syncStatus  the `/api/store/sync-status/` payload
 */
export function catalogImporting(syncStatus) {
  const sync = syncStatus?.sync;
  if (!sync || isRefreshSync(sync.syncType)) return false;
  return sync.status === 'queued' || CATALOG_PHASES.some((phase) => phase.status === sync.status);
}

/**
 * A run that tops up a store that already has its data, as opposed to
 * the first import. `scheduled` is what `trigger_catchup_sync` stamps
 * (shopify_app/services/initial_sync.py) — nightly recon, reinstall
 * catchup, the post-paywall return — and it is the difference between
 * "we are setting your store up" and "your numbers are stale", so the pill,
 * the price-wait screen and the Home tiles all ask it.
 */
export function isRefreshSync(syncType) {
  return syncType === 'scheduled';
}

/**
 * What a failed import asks of the merchant. Read off
 * `SyncRun.FailureReason` (shopify_app/models.py) — `sync.failureReason` on
 * the sync-status payload, `detail.import_refused` on the pricing read's 503.
 *
 *   - RECONNECT: Shopify refused the store's credentials. A reconnect is a new
 *     grant, and the grant starts the import again
 *     (shopify_app/services/import_start.py).
 *   - WAITING_FOR_SHOPIFY: Shopify refused the shop itself (frozen, locked,
 *     forbidden, gone). The watchdog starts the import again once Shopify
 *     answers.
 *   - FAILED: every other reason. A run that never started (`not_queued`,
 *     `queue_expired`) starts again at the next grant or sign-in and from the
 *     watchdog's hourly check; a crash (`error`) only from the paywall's
 *     pricing read (the table in shopify_app/services/import_start.py).
 */
export const FAILURE_STATE = {
  RECONNECT: 'reconnect',
  WAITING_FOR_SHOPIFY: 'waiting_for_shopify',
  FAILED: 'failed',
};

// Every SyncRun.FailureReason, and nothing else: pinned against the backend
// by shopify_app/tests/test_run_failure.py (ScreensKnowEveryReasonTests).
const STATE_BY_REASON = {
  credentials_refused: FAILURE_STATE.RECONNECT,
  shop_unavailable: FAILURE_STATE.WAITING_FOR_SHOPIFY,
  not_queued: FAILURE_STATE.FAILED,
  queue_expired: FAILURE_STATE.FAILED,
  error: FAILURE_STATE.FAILED,
};

/**
 * @param {string} reason  a failed run's SyncRun.FailureReason; the database
 *   refuses a failed run without one (`shopify_syncrun_reason_iff_failed`).
 */
export function failureState(reason) {
  if (!Object.hasOwn(STATE_BY_REASON, reason)) {
    console.error(`syncGate: unknown SyncRun failure reason ${String(reason)}`);
    return FAILURE_STATE.FAILED;
  }
  return STATE_BY_REASON[reason];
}

/** The import waits for Shopify access, and restarts once access is back. */
export function awaitsAccess(reason) {
  return failureState(reason) !== FAILURE_STATE.FAILED;
}

// The run's own phases take minutes, and the merchant works in the app while
// the pill counts them (or waits on the price-wait screen): every five seconds
// follows a phase closely enough.
const POLL_INTERVAL_ACTIVE = 5_000;
// The recovery check asks Shopify at most hourly
// (shopify_app/services/import_start.py ACCESS_RECHECK_INTERVAL); a minute
// shows its restart soon enough without loading the API.
const POLL_INTERVAL_AWAITING_ACCESS = 60_000;
// The orders land in the background after the run reads `completed`, for
// hours on a large store. A merchant waiting on their price sees the paywall
// within ten seconds of the history being in; one inside the app watches it
// land on the sidebar pill, where a count every 30 s is plenty.
const POLL_INTERVAL_PRICE_WAITING = 10_000;
const POLL_INTERVAL_ORDERS_LANDING = 30_000;
// The forecast run has a 270-minute budget (backend/core/scheduling/
// pipelines.py PIPELINE_BUDGETS); a read every 30 s sees it end soon enough.
const POLL_INTERVAL_FORECAST_CALCULATING = 30_000;
// A failed forecast run is re-run when the queue redelivers its message
// (backend/shopify_app/services/sync_status.py), and reads `running` again:
// the pill and the Home tiles must see that without a reload.
const POLL_INTERVAL_FORECAST_FAILED = 60_000;
// The store's first forecast is still owed and no run says when it comes: the
// served sync is a later one than the forecast run's, or the nightly builds
// it instead. A minute shows the Forecast page soon after, and the reads
// stop once it is in.
const POLL_INTERVAL_FORECASTS_MISSING = 60_000;

/**
 * The catalog is in and the order history is still landing, or its tail is
 * still applying the orders' attribution and starting the forecast run: the
 * owed import is on its way (`importOnItsWay`, backend/shopify_app/services/
 * import_start.py) after the run read `completed` (it does so at orders
 * submission). The sidebar's `SyncProgressPill` shows the orders landing for
 * exactly this window, so it hands over to the calculation with no gap.
 *
 * @param {object|null} syncStatus  the `/api/store/sync-status/` payload
 */
export function ordersLanding(syncStatus) {
  return !!syncStatus?.importOnItsWay && syncStatus.sync?.status === 'completed';
}

/**
 * The order history the store is owed is not in yet (`ordersSynced`) and its
 * import is on its way (`importOnItsWay`, backend/shopify_app/services/
 * import_start.py) — the run still syncing, or its orders still landing after
 * it read `completed`. A sum over the orders so far would read as the store's
 * (the Home Sales tile). Once the orders are in the sum is the store's, while
 * the import's tail still applies their attribution and starts the forecast
 * run (`ordersLanding`: the sidebar pill).
 *
 * @param {object|null} syncStatus  the `/api/store/sync-status/` payload
 */
export function orderHistoryImporting(syncStatus) {
  return syncStatus !== null && !!syncStatus.importOnItsWay && !syncStatus.ordersSynced;
}

/**
 * Where the forecast run the served sync started stands, told to the
 * merchant by the sidebar's `SyncProgressPill` and the Home forecast tiles
 * (`utils/kpiValueCoverage`).
 *
 *   - CALCULATING: the run is pending or running.
 *   - READY: it succeeded.
 *   - FAILED: it failed. The queue may still retry it, and a retry reads
 *     `running` again (backend/shopify_app/services/sync_status.py says what
 *     `failed` means).
 */
export const FORECAST_RUN = {
  CALCULATING: 'calculating',
  READY: 'ready',
  FAILED: 'failed',
};

// Every PipelineRunStatus (backend/core/models.py), and nothing else: pinned
// against the backend by shopify_app/tests/test_saas_sync_status.py
// (ScreensKnowEveryRunStatusTests).
// `partial` has no writer today; were one added, some steps failed.
const FORECAST_RUN_BY_STATUS = {
  pending: FORECAST_RUN.CALCULATING,
  running: FORECAST_RUN.CALCULATING,
  succeeded: FORECAST_RUN.READY,
  failed: FORECAST_RUN.FAILED,
  partial: FORECAST_RUN.FAILED,
};

/**
 * @param {object|null} syncStatus  the `/api/store/sync-status/` payload
 * @returns {string|null} a FORECAST_RUN, or null when nothing is read yet or
 *   the served sync started no forecast run (`onboarding: null`)
 */
export function forecastRunState(syncStatus) {
  if (syncStatus === null) return null;
  const { onboarding } = syncStatus;
  if (onboarding === null) return null;
  if (!Object.hasOwn(FORECAST_RUN_BY_STATUS, onboarding.status)) {
    // Shown as a failure: an unknown state must not look like nothing.
    console.error(`syncGate: unknown forecast run status ${String(onboarding.status)}`);
    return FORECAST_RUN.FAILED;
  }
  return FORECAST_RUN_BY_STATUS[onboarding.status];
}

/**
 * The store has no forecast yet (`forecastsGenerated`,
 * `Store.forecasts_generated`, on once the first forecast and classification
 * are in). The Forecast page is then not offered — the sidebar, the mobile
 * menu and the command palette leave it out — and its route says why instead
 * (`pages/Forecast.jsx`). The read is polled until the flag is on
 * (`syncPollInterval`), so the page appears without a reload.
 *
 * False when nothing is read: a store that is not a Shopify store has no
 * sync-status read (`hooks/useInAppSyncStatus`), and a Shopify store's read is
 * in before the app shows (`components/entry/entryScreen.js`).
 *
 * @param {object|null} syncStatus  the `/api/store/sync-status/` payload
 */
export function forecastsMissing(syncStatus) {
  return syncStatus?.forecastsGenerated === false;
}

/**
 * How often to re-read the sync-status payload, in ms, or false: every five
 * seconds while a run is queued or running, every minute while the latest run
 * failed on a Shopify refusal (a sign-in or the watchdog starts it again, and
 * the pill must then say "importing" without a reload), every ten seconds while
 * a merchant's PRICE waits on orders still landing after the run completed
 * (`priceWaitsOnImport`), every 30 seconds while those orders land behind a
 * merchant inside the app (`ordersLanding`: the sidebar pill's count), every
 * 30 seconds while the forecast run they started is calculating
 * (`forecastRunState`: the pill and the Home tiles see it end), every
 * minute while that run reads failed (the queue may re-run it), every
 * minute while a completed import's store still has no forecast
 * (`forecastsMissing`: the Forecast page appears once it is in), and never
 * otherwise.
 *
 * @param {object|null} syncStatus  the `/api/store/sync-status/` payload
 * @param {{accessDenied: boolean}} [context]  whether the merchant is held
 *   at the paywall, where the price waits on the import
 */
export function syncPollInterval(syncStatus, { accessDenied = false } = {}) {
  const sync = syncStatus?.sync;
  if (!sync) return false;
  if (sync.isRunning || sync.status === 'queued') return POLL_INTERVAL_ACTIVE;
  if (sync.status === 'failed' && awaitsAccess(sync.failureReason)) {
    return POLL_INTERVAL_AWAITING_ACCESS;
  }
  if (accessDenied && orderHistoryImporting(syncStatus)) return POLL_INTERVAL_PRICE_WAITING;
  if (ordersLanding(syncStatus)) return POLL_INTERVAL_ORDERS_LANDING;
  const forecastRun = forecastRunState(syncStatus);
  if (forecastRun === FORECAST_RUN.CALCULATING) return POLL_INTERVAL_FORECAST_CALCULATING;
  if (forecastRun === FORECAST_RUN.FAILED) return POLL_INTERVAL_FORECAST_FAILED;
  if (sync.status === 'completed' && forecastsMissing(syncStatus)) {
    return POLL_INTERVAL_FORECASTS_MISSING;
  }
  return false;
}

/**
 * Does a merchant without access wait on the import before they can be
 * priced? The price is derived from the order history, so: true while that
 * history is not in and its import is on its way (`orderHistoryImporting`),
 * and for a failed latest run of a store whose orders are not in: the
 * paywall's pricing read would start that import again on every load, so the
 * failure is shown instead, with what it asks (reconnect, wait for Shopify,
 * or support). The tail the import runs after the orders are in does not
 * hold the price.
 *
 * @param {object} syncStatus  the `/api/store/sync-status/` payload
 */
export function priceWaitsOnImport(syncStatus) {
  if (orderHistoryImporting(syncStatus)) return true;
  return !syncStatus.ordersSynced && syncStatus.sync?.status === 'failed';
}
